import EventEmitter from 'eventemitter3';
import uniqueId from 'lodash/uniqueId';

import { CandidateEvent, EventBusEvent, EventBusPayload, EventBusTransactionRecord } from './EventBus.types';

class EventBus {
  private static _instance: EventBus;
  private eventEmitter: EventEmitter;
  private activeTransactions: Map<string, EventBusTransactionRecord<EventBusEvent, EventBusPayload<EventBusEvent>>[]> =
    new Map();

  private constructor() {
    this.eventEmitter = new EventEmitter();
  }

  static getInstance(): EventBus {
    if (!this._instance) {
      this._instance = new EventBus();
    }
    return this._instance;
  }

  startTransaction(transactionKey?: string, payload?: EventBusPayload<CandidateEvent.StartTransaction>): string {
    const key = transactionKey ?? uniqueId('transaction-');

    if (this.activeTransactions.has(key)) {
      this.clearTransaction(key);
      console.warn(`Transaction with key "${key}" already exists. Clearing previous transaction.`);
    }

    const history: EventBusTransactionRecord<EventBusEvent, EventBusPayload<EventBusEvent>>[] = [];

    if (payload) {
      history.push({ event: CandidateEvent.StartTransaction, payload, timestamp: Date.now() });
    }

    this.activeTransactions.set(key, history);

    return key;
  }

  commitTransaction(transactionKey: string): void {
    const history = this.activeTransactions.get(transactionKey);

    if (!history) {
      console.warn(`No active transaction found for key "${transactionKey}"`);
      return;
    }

    this.eventEmitter.emit(CandidateEvent.CommitTransaction, {
      transactionKey,
      history: [...history].reverse(),
    });

    this.activeTransactions.delete(transactionKey);
  }

  clearTransaction(transactionKey: string): void {
    this.activeTransactions.delete(transactionKey);
  }

  getTransactionHistory<E extends EventBusEvent>(
    transactionKey: string,
  ): EventBusTransactionRecord<E, EventBusPayload<E>>[] | undefined {
    return this.activeTransactions.get(transactionKey) as EventBusTransactionRecord<E, EventBusPayload<E>>[];
  }

  isTransactionActive(transactionKey: string): boolean {
    return this.activeTransactions.has(transactionKey);
  }

  getActiveTransactionKeys(): string[] {
    return Array.from(this.activeTransactions.keys());
  }

  emit<E extends EventBusEvent>(event: E, payload: EventBusPayload<E>, transactionKey?: string) {
    if (transactionKey && this.activeTransactions.has(transactionKey)) {
      const history = this.activeTransactions.get(transactionKey)!;

      history.push({
        event,
        payload,
        timestamp: Date.now(),
      });
    }

    this.eventEmitter.emit(event, payload, transactionKey);
  }

  on<E extends EventBusEvent>(event: E, listener: (payload: EventBusPayload<E>, transactionKey?: string) => void) {
    this.eventEmitter.on(event, listener);

    return () => {
      this.off(event, listener);
    };
  }

  off<E extends EventBusEvent>(event: E, listener: (payload: EventBusPayload<E>) => void) {
    this.eventEmitter.off(event, listener);
  }
}

const eventBus = EventBus.getInstance();

export { eventBus as EventBus };
